import { useState, useCallback, useEffect } from 'react';
import { useUserFetch } from '../api/fetch';
import { useNotify } from '../components/NotificationProvider';

export interface Video {
  id: string;
  videoName: string;
  fileName: string;
}

export interface VideoStatements {
  statement: boolean;
  videoEndTime: string | null;
  maxPlayedSeconds: number | null;
  testStatement: string | null;
  testContent: string | null;
}

export interface UseVideoDataProps {
  onError?: (message: string) => void;
  // heartbeat 送信時に現在の再生位置(秒)を取得する(未指定なら位置は送らない)
  getPlayedSeconds?: () => number | null;
}

export const useVideoData = ({ onError, getPlayedSeconds }: UseVideoDataProps = {}) => {
  const notify = useNotify();
  const handleError = onError ?? notify;
  const [videos, setVideos] = useState<Video[]>([]);
  const [statements, setStatements] = useState<Record<string, string>>({});
  const [videoEndDates, setVideoEndDates] = useState<Record<string, string>>({});
  const [testStatements, setTestStatements] = useState<Record<string, string>>({});
  const [testCreatedDates, setTestCreatedDates] = useState<Record<string, string>>({});
  const [testContents, setTestContents] = useState<Record<string, string>>({});
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [videoId, setVideoId] = useState<string | null>(null);
  
  const { get, post } = useUserFetch();

  

  // 講座に含まれる動画リストを取得
  const fetchVideos = useCallback(async (lectureId: string) => {
    try {
      const content = await get<{
        message: string;
        videos: Video[];
      }>(`/api/user/lecture/${lectureId}`);
      
      console.log(content.message);
      setVideos(content.videos);
      
      // 動画リストを取得した後に視聴状況を更新
      await refreshStatements(content.videos);
      return true;
    } catch (error) {
      handleError("Can't get data from db properly.");
      return false;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 動画の視聴状況を取得
  const getVideoStatement = useCallback(async (videoId: string) => {
    try {
      return await get<VideoStatements>(`/api/user/video/statement/${videoId}`);
    } catch (error) {
      handleError("Can't get data from db properly.");
      return null;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 動画のURLを取得
  const getVideoUrl = useCallback(async (fileName: string) => {
    try {
      const response = await get<{ url: string }>(`/api/user/video/${fileName}`);
      console.log(response.url);
      setVideoUrl(response.url);
      return response.url;
    } catch (error) {
      handleError("Can't get Video from db properly.");
      return null;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 全動画の視聴状況を更新
  const refreshStatements = useCallback(async (videoList?: Video[]) => {
    const targetVideos = videoList || videos;
    if (targetVideos.length === 0) return;
    
    const newStatements: Record<string, string> = {};
    const newVideoEndDates: Record<string, string> = {};
    const newTestStatements: Record<string, string> = {};
    const newTestCreatedDates: Record<string, string> = {};
    const newTestContents: Record<string, string> = {};

    for (const video of targetVideos) {
      const result = await getVideoStatement(video.id);
      
      if (result) {
        newStatements[video.id] = result.statement ? "完了" : "未完了";
        newVideoEndDates[video.id] = result.videoEndTime !== null ? result.videoEndTime : "-";
        newTestStatements[video.id] = result.testStatement !== null ? "受験済" : "未受験";
        newTestCreatedDates[video.id] = result.testStatement !== null ? result.testStatement : "-";
        newTestContents[video.id] = result.testContent !== null ? result.testContent : "";
      }
    }

    setStatements((prev) => ({ ...prev, ...newStatements }));
    setVideoEndDates((prev) => ({ ...prev, ...newVideoEndDates }));
    setTestStatements((prev) => ({ ...prev, ...newTestStatements }));
    setTestCreatedDates((prev) => ({ ...prev, ...newTestCreatedDates }));
    setTestContents((prev) => ({ ...prev, ...newTestContents }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videos]);

  type postType = (typeof apis)[keyof typeof apis];

  // heartbeat/stop には現在の再生位置(秒)を載せ、サーバ側で最大再生位置として記録する
  // (初回視聴のシーク禁止のサーバ側検証に使われる)。
  const heartbeatBody = (time: string) => {
    const playedSeconds = getPlayedSeconds ? getPlayedSeconds() : null;
    return {
      heartbeat_time: time,
      ...(playedSeconds != null ? { played_seconds: playedSeconds } : {}),
    };
  };

  const apis = {
    start: {
      type: 'start',
      getUri: (id: string) => `/api/user/video/start/${id}`,
      body: (time: string) => ({ start_time: time }),
    },
    heartbeat: {
      type: 'heartbeat',
      getUri: (id: string) => `/api/user/video/heartbeat/${id}`,
      body: heartbeatBody,
    },
    stop: {
      type: 'stop',
      getUri: (id: string) => `/api/user/video/heartbeat/${id}`,
      body: heartbeatBody,
    },
    end: {
      type: 'end',
      getUri: (id: string) => `/api/user/video/end/${id}`,
      body: (time: string) => ({ end_time: time, statement: true }),
    },
  } as const;

  const postWrapper = useCallback(async (
    api: postType,
    videoId: string | null
  ): Promise<boolean> => {
    if (!videoId) return false;
    const time = new Date().toISOString();

    try {
      await post(api.getUri(videoId), {
        body: JSON.stringify(api.body(time)),
      });

      switch (api.type) {
        case "end":
          await refreshStatements();
          setVideoId(null);
          break;
        case "stop":
          setVideoId(null);
          break;
      }

      return true;
    } catch (error) {
      handleError(`Failed to call ${api.type}`);
      return false;
    }
  }, [post, handleError, refreshStatements]);

  // 視聴開始
  const recordVideoStart = (videoId: string) => {
    setVideoId(videoId);
    return postWrapper(apis.start, videoId);
  };

  // 視聴終了
  const recordVideoEnd = () => {
    return postWrapper(apis.end, videoId);
  };

  // 視聴停止
  const recordVideoStop = useCallback(async () => {
    return postWrapper(apis.stop, videoId);
  }, [videoId, apis.stop, postWrapper]);

  // サーバに stop を送らずにクライアント側の視聴セッションだけ終了する。
  // 深夜ブロックの強制停止用(境界以降はサーバが heartbeat を拒否するため送っても無駄打ちになる)。
  const clearVideoSession = useCallback(() => {
    setVideoId(null);
  }, []);

  // heartbeat を自動送信
  useEffect(() => {
    const interval = setInterval(async () => {
      const success = await postWrapper(apis.heartbeat, videoId);
      return success;
    }, 60 * 1000);
    return () => clearInterval(interval);
  }, [videoId, apis.heartbeat, postWrapper]);

  // ページ離脱時の視聴時間記録終了処理
  useEffect(() => {
    const handleBeforeUnload = () => recordVideoStop();
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [recordVideoStop]);

  // テスト結果送信
  const submitTestResult = useCallback(async (videoId: string, answer: string) => {
    if (answer.length > 200) {
      return { success: false, message: "Answer is too long", status: "error" };
    }

    try {
      const time = new Date();
      const japanTime = new Date(time.getTime() + 9 * 60 * 60 * 1000);
      const createdAt = japanTime.toISOString();
      console.log("Test created at :" + createdAt);

      const response = await post<{ status: string; message: string }>(`/api/user/video/test/${videoId}`, {
        body: JSON.stringify({
          answer: answer,
          created_at: createdAt,
        }),
      });

      if (response.status === "uvt1") {
        console.log(response.message);
        setTestContents((prev) => ({
          ...prev,
          [videoId]: answer,
        }));
        await refreshStatements();
        return { success: true, message: "Test submitted successfully", status: "new" };
      } else if (response.status === "uvt4") {
        console.log(response.message);
        setTestContents((prev) => ({
          ...prev,
          [videoId]: answer,
        }));
        await refreshStatements();
        return { success: true, message: "Test updated successfully", status: "update" };
      } else {
        console.log(response.status);
        console.log(response.message);
        return { success: false, message: response.message, status: "error" };
      }
    } catch (error) {
      handleError(`Error submitting test result: ${error}`);
      return { success: false, message: `Error: ${error}`, status: "error" };
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshStatements]);

  

  return {
    videos,
    statements,
    videoEndDates,
    testStatements,
    testCreatedDates,
    testContents,
    videoUrl,
    fetchVideos,
    getVideoStatement,
    getVideoUrl,
    refreshStatements,
    recordVideoStart,
    recordVideoEnd,
    recordVideoStop,
    clearVideoSession,
    submitTestResult
  };
};
